import type { FolderResponseDto } from '@inference.engine/workspace-api-client/web';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { badgeVariants } from '@monksflow/monks-ui';
import { FileTextIcon } from 'lucide-react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useHasProjectPermission } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';

/**
 * A badge does not shrink, so a long title would push the header wider rather
 * than give way to it. This is the width past which the title truncates.
 */
const TITLE_MAX_WIDTH = 'max-w-37.5';

interface FolderBriefBadgeProps {
  projectId: string;
  folder: Pick<FolderResponseDto, 'parentId' | 'brief'>;
  /** Opens the folder editor, the one place a brief is chosen or cleared. */
  onAddBrief: () => void;
}

/**
 * The brief a folder delivers against, as a badge beside the folder's name.
 *
 * Two states and nothing in between: it names the brief and opens it, or it
 * invites one and opens the editor. Nothing is written from here, which is
 * what separates it from the menu it replaced — choosing and clearing a brief
 * happen in the folder editor now, beside every other field of the folder.
 */
export function FolderBriefBadge(props: FolderBriefBadgeProps) {
  const { t } = useTranslation('projects');
  const canEditFolder = useHasProjectPermission([PROJECT_PERMISSIONS.ProjectUploadAssets]);
  const brief = props.folder.brief ?? null;

  if (brief) {
    return (
      // `badgeVariants` rather than `Badge`, which is a span with no
      // `asChild`: the badge has to be the link itself, so the brief is
      // reachable by keyboard and the whole pill is the target.
      <Link
        to={ROUTES.PROJECT_BRIEF_DETAIL(props.projectId, brief.id)}
        title={brief.title}
        aria-label={t('manageProjectFolders.folderBriefBadge.openBrief', { title: brief.title })}
        className={badgeVariants({
          variant: 'blue',
          roundness: 'full',
          className: 'min-w-0 overflow-hidden',
        })}
        data-testid="folder-brief-badge-link"
      >
        <FileTextIcon className="size-3.5 shrink-0" />
        <span className={`truncate ${TITLE_MAX_WIDTH}`}>{brief.title}</span>
      </Link>
    );
  }

  // A subfolder delivers against its root ancestor's brief and the API refuses
  // one of its own, so the invitation would open an editor with no field to
  // answer it. The same shape covers someone without the permission every
  // folder mutation answers to.
  if (Boolean(props.folder.parentId) || !canEditFolder) return null;

  return (
    <button
      type="button"
      onClick={props.onAddBrief}
      className={badgeVariants({
        variant: 'outline',
        roundness: 'full',
        className: 'cursor-pointer hover:bg-accent',
      })}
      data-testid="folder-brief-badge-add"
    >
      <FileTextIcon className="size-3.5 shrink-0" />
      {t('manageProjectFolders.folderBriefBadge.addBrief')}
    </button>
  );
}
